import { useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer,
} from "recharts";
import { ReceiptText, AlertCircle } from "lucide-react";
import { Loader } from "@/components/ui/Loader";
import { PedidoCard } from "@/services/suprimentos";
import { PedidoValorStat, computePedidosPorValor } from "../chartUtils";
import { formatCurrency } from "@/utils/format";
import { BAR_HOVER_PROPS, barRowFromClick } from "@/utils/rechartsInteraction";
import { PedidoItensModal } from "../PedidoItensModal";

/* ─── Tooltip do gráfico ─────────────────────────────────────────────────── */
function BarTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload: PedidoValorStat; value: number }>;
}) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  const p = d.pedido;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 max-w-[260px]">
      <p className="text-xs font-bold text-slate-800 mb-0.5">{d.label}</p>
      {p.supplier && <p className="text-xs text-slate-500 truncate mb-1">{p.supplier}</p>}
      <p className="text-sm font-bold" style={{ color: "#B03858" }}>{formatCurrency(d.valor)}</p>
      {p.itemCount > 0 && (
        <p className="text-xs text-slate-400 mt-0.5">{p.itemCount} insumo{p.itemCount !== 1 ? "s" : ""}</p>
      )}
    </div>
  );
}

/* ─── Componente principal ───────────────────────────────────────────────── */
interface Props {
  pedidos: PedidoCard[];
  loading: boolean;
}

export function PedidosPorValor({ pedidos, loading }: Props) {
  const [selected, setSelected] = useState<PedidoCard | null>(null);

  const data = computePedidosPorValor(pedidos);

  // Clique direto na barra — Recharts passa o dado do entry como 1º argumento
  const handleBarClick = (item: unknown) => {
    const entry = barRowFromClick<PedidoValorStat>(item);
    if (entry?.pedido) setSelected(entry.pedido);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <div className="flex items-center gap-2 mb-4">
          <ReceiptText className="w-4 h-4 text-brand-600" />
          <h3 className="text-sm font-semibold text-slate-700">Pedidos por Valor</h3>
        </div>
        <div className="h-56 flex flex-col items-center justify-center gap-3 text-slate-400">
          <Loader size="md" label="Carregando pedidos…" />
        </div>
      </div>
    );
  }

  if (!data.length) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <div className="flex items-center gap-2 mb-3">
          <ReceiptText className="w-4 h-4 text-brand-600" />
          <h3 className="text-sm font-semibold text-slate-700">Pedidos por Valor</h3>
        </div>
        <div className="h-40 flex items-center justify-center text-sm text-slate-400">
          Nenhum pedido encontrado no período selecionado.
        </div>
      </div>
    );
  }

  const chartHeight = Math.max(280, data.length * 32 + 40);

  return (
    <>
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ReceiptText className="w-4 h-4 text-brand-600" />
            <h3 className="text-sm font-semibold text-slate-700">Pedidos por Valor</h3>
            <span className="text-xs text-slate-400 font-normal ml-1">(top {data.length})</span>
          </div>
          <span className="text-xs text-slate-400 bg-slate-50 px-2 py-0.5 rounded-full">
            {formatCurrency(data.reduce((s, d) => s + d.valor, 0))} total
          </span>
        </div>

        <p className="text-xs text-slate-400 -mt-1">
          Clique em um pedido para visualizar os insumos detalhados.
        </p>

        <ResponsiveContainer width="100%" height={chartHeight}>
          <BarChart
            data={data}
            layout="vertical"
            margin={{ left: 8, right: 80, top: 4, bottom: 4 }}
          >
            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
            <XAxis
              type="number"
              tickFormatter={(v: number) => formatCurrency(v, true)}
              tick={{ fontSize: 10, fill: "#94a3b8" }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              type="category"
              dataKey="label"
              width={80}
              tick={{ fontSize: 10, fill: "#64748b" }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip content={<BarTooltip />} cursor={{ fill: "#fdf2f5" }} />
            <Bar
              {...BAR_HOVER_PROPS}
              dataKey="valor"
              name="Valor"
              fill="#df4870"
              radius={[0, 4, 4, 0]}
              maxBarSize={24}
              onClick={handleBarClick}
              cursor="pointer"
              label={{
                position: "right",
                formatter: (v: unknown) => formatCurrency(Number(v ?? 0), true),
                fontSize: 10,
                fill: "#8e3048",
              }}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {selected && (
        <PedidoItensModal pedido={selected} onClose={() => setSelected(null)} />
      )}

      {/* Alerta para pedidos sem valor */}
      {!loading && pedidos.some((p) => (p.value ?? 0) === 0) && (
        <p className="text-xs text-slate-400 flex items-center gap-1">
          <AlertCircle className="w-3.5 h-3.5" />
          Pedidos com valor R$ 0,00 não são exibidos no gráfico.
        </p>
      )}
    </>
  );
}
